import React, { useLayoutEffect, useRef, useState } from "react";

const fmtNoteTime = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  if (isNaN(d)) return "";
  const date = d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${date}, ${time.toUpperCase()}`;
};

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

function Avatar({ name, picture }) {
  const [broken, setBroken] = useState(false);
  if (picture && !broken) {
    return (
      <img
        src={picture}
        alt=""
        onError={() => setBroken(true)}
        className="w-9 h-9 rounded-full object-cover flex-shrink-0 bg-gray-100"
      />
    );
  }
  return (
    <div className="w-9 h-9 rounded-full flex-shrink-0 bg-[#002169] text-white text-xs font-bold flex items-center justify-center">
      {initials(name)}
    </div>
  );
}

function Note({ note }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const bodyRef = useRef(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [note.comment]);

  return (
    <div className="flex gap-3 px-4 py-4 border-b border-gray-100 last:border-b-0">
      <Avatar name={note.author_name} picture={note.author_picture} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">{note.author_name || "Staff"}</span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-500">{fmtNoteTime(note.created_at)}</span>
        </div>
        <p
          ref={bodyRef}
          className={`text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words mt-1 ${
            expanded ? "" : "line-clamp-2"
          }`}
        >
          {note.comment}
        </p>
        {(clamped || expanded) && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-0.5 text-xs text-gray-600 underline underline-offset-2 hover:text-gray-900"
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    </div>
  );
}

const ChemistNotesFeed = ({ notes = [], onAdd, adding }) => {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);
  const wrapRef = useRef(null);

  // Fill exactly the space left below the page's sticky header so the feed
  // scrolls internally and the page itself does not.
  useLayoutEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setMaxHeight(Math.max(320, window.innerHeight - top - 16));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const submit = async () => {
    const value = text.trim();
    if (!value || adding) return;
    await onAdd(value);
    setText("");
    setFocused(false);
  };

  return (
    <div
      ref={wrapRef}
      style={maxHeight ? { height: maxHeight } : undefined}
      className="rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col"
    >
      <div className="bg-gray-50 border-b border-gray-200 p-3 flex-shrink-0">
        <textarea
          rows={focused || text ? 3 : 1}
          value={text}
          onFocus={() => setFocused(true)}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="What's this note about?"
          disabled={adding}
          className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002169]/25 focus:border-[#002169]/40 disabled:opacity-50"
        />
        {(focused || text) && (
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => { setText(""); setFocused(false); }}
              className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!text.trim() || adding}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-[#002169] rounded-lg hover:bg-[#001550] disabled:opacity-40"
            >
              {adding ? "Saving…" : "Add note"}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {notes.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-gray-400 italic">No notes yet.</p>
        ) : (
          notes.map((n) => <Note key={n.id} note={n} />)
        )}
      </div>
    </div>
  );
};

export default ChemistNotesFeed;
